import type { Timecard } from '../types';
import { formatCompanyDate } from './companyDateFormat';

export type TimecardReportStatusFilter = 'all' | 'approved' | 'unapproved';

export interface TimecardReportCommonFilters {
  employeeIds: number[];
  scheduleIds: number[];
  positionIds: number[];
  statusFilter?: TimecardReportStatusFilter;
  employeeLabels?: string[];
  scheduleLabels?: string[];
  positionLabels?: string[];
}

export function formatTimecardReportStatusLabel(
  status: TimecardReportStatusFilter | undefined
): string {
  if (status === 'approved') return 'Approved';
  if (status === 'unapproved') return 'UnApproved';
  return 'All';
}

function matchesStatusFilter(
  timecard: Timecard,
  statusFilter: TimecardReportStatusFilter | undefined
): boolean {
  const status = statusFilter ?? 'all';
  if (status === 'approved') return timecard.isApproved === true;
  if (status === 'unapproved') return timecard.isApproved !== true;
  return true;
}

/** Status, employee, schedule, and position filters (day-level for schedule/position). */
export function filterTimecardsByCommonFilters(
  timecards: Timecard[],
  filters: TimecardReportCommonFilters
): Timecard[] {
  return timecards
    .filter((tc) => {
      if (!matchesStatusFilter(tc, filters.statusFilter)) return false;
      if (filters.employeeIds.length > 0 && !filters.employeeIds.includes(tc.employeeId)) {
        return false;
      }
      return true;
    })
    .map((tc) => {
      let days = tc.days ?? [];

      if (filters.scheduleIds.length > 0) {
        days = days.filter(
          (d) => d.scheduleId != null && filters.scheduleIds.includes(d.scheduleId)
        );
      }

      if (filters.positionIds.length > 0) {
        days = days.filter(
          (d) => d.positionId != null && filters.positionIds.includes(d.positionId)
        );
      }

      return { ...tc, days };
    })
    .filter((tc) => tc.days.length > 0);
}

export function filterTimecardsByWeekRange(
  timecards: Timecard[],
  fromWeekStartDate: string,
  toWeekStartDate: string
): Timecard[] {
  const from = new Date(fromWeekStartDate);
  const to = new Date(toWeekStartDate);

  return timecards.filter((tc) => {
    const weekDate = new Date(tc.weekStartDate);
    return weekDate >= from && weekDate <= to;
  });
}

export function formatCommonFiltersSummary(filters: TimecardReportCommonFilters): string {
  const parts: string[] = [];

  if (filters.statusFilter && filters.statusFilter !== 'all') {
    parts.push(`Status: ${formatTimecardReportStatusLabel(filters.statusFilter)}`);
  }

  if (filters.employeeLabels?.length) {
    parts.push(`Employees: ${filters.employeeLabels.join(', ')}`);
  } else if (filters.employeeIds?.length) {
    parts.push(`${filters.employeeIds.length} employee${filters.employeeIds.length === 1 ? '' : 's'}`);
  }

  if (filters.scheduleLabels?.length) {
    parts.push(`Schedules: ${filters.scheduleLabels.join(', ')}`);
  } else if (filters.scheduleIds?.length) {
    parts.push(`${filters.scheduleIds.length} schedule${filters.scheduleIds.length === 1 ? '' : 's'}`);
  }

  if (filters.positionLabels?.length) {
    parts.push(`Positions: ${filters.positionLabels.join(', ')}`);
  } else if (filters.positionIds?.length) {
    parts.push(`${filters.positionIds.length} position${filters.positionIds.length === 1 ? '' : 's'}`);
  }

  return parts.join(' · ');
}

export function commonFiltersFromParameters(
  parameters: Record<string, unknown> | undefined | null
): TimecardReportCommonFilters {
  const p = parameters ?? {};
  return {
    employeeIds: Array.isArray(p.employeeIds) ? (p.employeeIds as number[]) : [],
    scheduleIds: Array.isArray(p.scheduleIds) ? (p.scheduleIds as number[]) : [],
    positionIds: Array.isArray(p.positionIds) ? (p.positionIds as number[]) : [],
    statusFilter: (p.statusFilter as TimecardReportStatusFilter) ?? 'all',
    employeeLabels: Array.isArray(p.employeeLabels) ? (p.employeeLabels as string[]) : undefined,
    scheduleLabels: Array.isArray(p.scheduleLabels) ? (p.scheduleLabels as string[]) : undefined,
    positionLabels: Array.isArray(p.positionLabels) ? (p.positionLabels as string[]) : undefined,
  };
}

export function formatWeekRangeLabel(startDate: string): string {
  const start = new Date(startDate);
  const end = new Date(startDate);
  end.setDate(start.getDate() + 6);
  return `${formatCompanyDate(start)} - ${formatCompanyDate(end)}`;
}

export function commonFiltersMatch(
  a: TimecardReportCommonFilters,
  b: TimecardReportCommonFilters
): boolean {
  const sortNums = (xs: number[]) => [...xs].sort((x, y) => x - y).join(',');
  return (
    (a.statusFilter ?? 'all') === (b.statusFilter ?? 'all') &&
    sortNums(a.employeeIds) === sortNums(b.employeeIds) &&
    sortNums(a.scheduleIds) === sortNums(b.scheduleIds) &&
    sortNums(a.positionIds) === sortNums(b.positionIds)
  );
}
